import React, { useState } from 'react'
import 'react-markdown-editor-lite/lib/index.css'
import {
  UploadOutlined,
  PlusOutlined,
  DeleteOutlined,
  TagOutlined,
  SaveOutlined,
  EditOutlined,
  CheckCircleOutlined,
  PictureOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons'
import {
  Modal,
  Form,
  Radio,
  Upload,
  Button,
  Select,
  Input,
  Card,
  Tag,
  Tooltip,
  Popconfirm,
} from 'antd'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { GET_BRAVO_TEMPLATES, GET_WHATSAPP_TEMPLATES } from '../graphql/Queries'
import { SAVE_WHATSAPP_TEMPLATE, DELETE_WHATSAPP_TEMPLATE } from '../graphql/Mutations'
import openNotification from 'utils/Notification'
import Page404 from 'components/Page404'
import ReactQuill from 'react-quill'

const { Option } = Select

const TEMPLATE_CONFIGS = {
  master_client_meet_welcome: {
    title: 'master_client_meet_welcome',
    hasHeaderImage: true,
    imageUrl:
      'https://d3jt6ku4g6z5l8.cloudfront.net/IMAGE/6353da2e153a147b991dd812/4958901_highanglekidcheatingschooltestmin.jpg',
    params: [
      { key: 'param1', label: '1. Date', default: '05-08-2026', placeholder: 'Enter Date (e.g. 05-08-2026)' },
      { key: 'param2', label: '2. Time', default: '4:00 PM', placeholder: 'Enter Time (e.g. 4:00 PM)' },
      { key: 'param3', label: '3. Link', default: 'https://docs.google.com/forms/d/e/1FAIpQLSdfM3TuWm2LDibq-IsKH-kY5CxUJxE16O4PE259NGIZN_dmdA/viewform', placeholder: 'Enter Form Link' }
    ],
    templateBody: `Dear Investor,

This is a reminder about your upcoming ILTS Customer Meet session:

📅 Date: [{{1}}]
⏰ Time: [{{2}}]

If you have questions regarding your investments, you may share them using this form:
[{{3}}]

Meeting details:
🔗 Zoom Link: finideas.com/ILTSReviewMeeting
🆔 Meeting ID: 825 8799 5085
🔑 Passcode: 331595

This session is exclusively for Finideas customers. Kindly join on time.

Thank you,
Team Finideas`
  },
  master_client_meet_reminder: {
    title: 'master_client_meet_reminder',
    hasHeaderImage: true,
    imageUrl:
      'https://d3jt6ku4g6z5l8.cloudfront.net/IMAGE/6353da2e153a147b991dd812/4958901_highanglekidcheatingschooltestmin.jpg',
    params: [
      { key: 'param1', label: '1. Text Wording', default: '1 day to go', placeholder: 'Enter Text Wording (e.g. 1 day to go)' },
      { key: 'param2', label: '2. Date', default: 'Saturday, 27 Sept - 4:00 PM', placeholder: 'Enter Date' },
      { key: 'param3', label: '3. Time', default: '4:00 PM', placeholder: 'Enter Time' },
      { key: 'param4', label: '4. Link', default: 'https://forms.gle/e8hrFUYeZa5QDcpP6', placeholder: 'Enter Form Link' }
    ],
    templateBody: `Dear Investor,

[{{1}}]

This is a reminder about your upcoming ILTS Customer Meet session:

📅 Date: [{{2}}]
⏰ Time: [{{3}}]

If you have questions regarding your investments, you may share them using this form:
[{{4}}]

Meeting details:
🔗 Zoom Link: finideas.com/ILTSReviewMeeting
🆔 Meeting ID: 825 8799 5085
🔑 Passcode: 331595

This session is exclusively for Finideas customers. Kindly join on time.

Thank you,
Team Finideas`
  },
  master_client_meet_reminder_finalcall: {
    title: 'master_client_meet_reminder_finalcall',
    hasHeaderImage: true,
    imageUrl:
      'https://d3jt6ku4g6z5l8.cloudfront.net/IMAGE/6353da2e153a147b991dd812/4958901_highanglekidcheatingschooltestmin.jpg',
    params: [
      { key: 'param1', label: '1. Time', default: '4:00 PM', placeholder: 'Enter Time (e.g. 4:00 PM)' },
      { key: 'param2', label: '2. Link', default: 'https://docs.google.com/forms/d/e/1FAIpQLSdfM3TuWm2LDibq-IsKH-kY5CxUJxE16O4PE259NGIZN_dmdA/viewform', placeholder: 'Enter Form Link' }
    ],
    templateBody: `Dear Investor,

Customer Meet session staring soon

⏰ Time: [{{1}}]

If you have questions regarding your investments, you may share them using this form:
[{{2}}]

Meeting details:
🔗 Zoom Link: finideas.com/ILTSReviewMeeting
🆔 Meeting ID: 825 8799 5085
🔑 Passcode: 331595

This session is exclusively for Finideas customers. Kindly join on time.

Thank you,
Team Finideas`
  }
}

const processImageForWhatsApp = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = (uploadEvent) => {
      const img = new Image()
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas')
          canvas.width = img.naturalWidth
          canvas.height = img.naturalHeight
          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0)

          const isPng = file.type === 'image/png' || file.name.toLowerCase().endsWith('.png')
          const mimeType = isPng ? 'image/png' : 'image/jpeg'
          const ext = isPng ? '.png' : '.jpg'

          canvas.toBlob(
            (blob) => {
              if (!blob) return resolve(file)
              const cleanBase = file.name
                .replace(/\.[^/.]+$/, '')
                .replace(/[^a-zA-Z0-9_-]/g, '_')
              const normalizedFile = new File([blob], `${cleanBase}${ext}`, {
                type: mimeType,
              })
              resolve(normalizedFile)
            },
            mimeType,
            0.92
          )
        } catch (e) {
          resolve(file)
        }
      }
      img.onerror = () => resolve(file)
      img.src = uploadEvent.target.result
    }
    reader.onerror = () => resolve(file)
    reader.readAsDataURL(file)
  })
}

const CollectionCreateForm = ({ visible, onSend, onCancel }) => {
  const [form] = Form.useForm()
  const [type, setType] = useState('EMAIL')
  const [fileList, setFileList] = useState(undefined)
  const [value, setValue] = useState('')
  const [templateId, setTemplateId] = useState(undefined)
  const [selectedWhatsAppTemplate, setSelectedWhatsAppTemplate] = useState(undefined)
  const [isCustomCampaign, setIsCustomCampaign] = useState(false)
  const [customCampaignName, setCustomCampaignName] = useState('')
  const [dynamicParams, setDynamicParams] = useState([])
  const [activeParamIndex, setActiveParamIndex] = useState(0)
  const [isEditingTemplate, setIsEditingTemplate] = useState(false)
  const [hasHeaderImage, setHasHeaderImage] = useState(false)
  const [templateImageUrl, setTemplateImageUrl] = useState('')
  const [templateImageFile, setTemplateImageFile] = useState(null)
  const [imagePreviewUrl, setImagePreviewUrl] = useState('')

  function resetForm() {
    form.resetFields()
    setSelectedWhatsAppTemplate(undefined)
    setIsCustomCampaign(false)
    setIsEditingTemplate(false)
    setCustomCampaignName('')
    setDynamicParams([])
    setHasHeaderImage(false)
    setTemplateImageUrl('')
    setTemplateImageFile(null)
    setImagePreviewUrl('')
    setValue('')
    setTemplateId(undefined)
  }

  const { data, loading, error } = useQuery(GET_BRAVO_TEMPLATES, {
    fetchPolicy: 'network-only',
  })

  const {
    data: whatsAppData,
    refetch: refetchWhatsAppTemplates,
  } = useQuery(GET_WHATSAPP_TEMPLATES, {
    fetchPolicy: 'network-only',
  })

  const [saveTemplateMutation, { loading: savingTemplate }] = useMutation(SAVE_WHATSAPP_TEMPLATE)
  const [deleteTemplateMutation] = useMutation(DELETE_WHATSAPP_TEMPLATE)

  const templatesMap = { ...TEMPLATE_CONFIGS }
  if (whatsAppData?.getWhatsAppTemplates) {
    whatsAppData.getWhatsAppTemplates.forEach((t) => {
      if (t && t.name) {
        templatesMap[t.name] = {
          title: t.name,
          isSaved: true,
          hasHeaderImage: Boolean(t.hasHeaderImage),
          imageUrl: t.imageUrl || '',
          params: (t.params || []).map((p, idx) => ({
            key: p.key || `param${idx + 1}`,
            label: p.label || `Parameter ${idx + 1}`,
            default: p.value || '',
          })),
        }
      }
    })
  }

  const allWhatsAppTemplates = Object.keys(templatesMap)

  const handleTemplateChange = (tmplName) => {
    setSelectedWhatsAppTemplate(tmplName)
    form.setFieldsValue({ whatsAppTemplate: tmplName })
    setTemplateImageFile(null)
    if (tmplName === 'CUSTOM_CAMPAIGN') {
      setIsCustomCampaign(true)
      setIsEditingTemplate(true)
      setCustomCampaignName('')
      setDynamicParams([])
      setHasHeaderImage(false)
      setTemplateImageUrl('')
      setImagePreviewUrl('')
    } else {
      setIsCustomCampaign(false)
      setIsEditingTemplate(false)
      setCustomCampaignName(tmplName)
      const config = templatesMap[tmplName]
      const tmplHasImg = Boolean(config?.hasHeaderImage)
      const tmplImg = config?.imageUrl || ''
      setHasHeaderImage(tmplHasImg)
      setTemplateImageUrl(tmplImg)
      setImagePreviewUrl(tmplImg)
      if (config?.params) {
        const initialParams = config.params.map((p, idx) => ({
          id: `p_${idx + 1}`,
          label: (p.label || `Parameter ${idx + 1}`).replace(/^\d+\.\s*/, ''),
          value: p.default || '',
        }))
        setDynamicParams(initialParams)
      } else {
        setDynamicParams([])
      }
    }
  }

  const handleSaveTemplate = () => {
    const finalName = (isCustomCampaign ? customCampaignName : selectedWhatsAppTemplate || '').trim()
    if (!finalName || finalName === 'CUSTOM_CAMPAIGN') {
      openNotification('error', 'Please enter Campaign Name!')
      return
    }

    if (hasHeaderImage && !templateImageFile && !templateImageUrl) {
      openNotification('error', 'Please select an image for the Image Header or select No!')
      return
    }

    const formattedParams = dynamicParams.map((p, idx) => ({
      id: p.id,
      key: `param${idx + 1}`,
      label: (p.label || `Parameter ${idx + 1}`).trim(),
      value: (p.value || '').trim(),
    }))

    const cleanName = finalName.toLowerCase().replace(/\s+/g, '_')

    const saveInput = {
      name: cleanName,
      params: formattedParams,
      hasHeaderImage: Boolean(hasHeaderImage),
      imageUrl: hasHeaderImage ? templateImageUrl : null,
    }

    if (hasHeaderImage && templateImageFile) {
      saveInput.file = templateImageFile
    }

    saveTemplateMutation({
      variables: {
        data: saveInput,
      },
    })
      .then((res) => {
        openNotification('success', `Template "${cleanName}" saved successfully!`)
        const savedTmpl = res?.data?.saveWhatsAppTemplate
        if (savedTmpl?.imageUrl) {
          setTemplateImageUrl(savedTmpl.imageUrl)
          setImagePreviewUrl(savedTmpl.imageUrl)
        }
        setTemplateImageFile(null)
        refetchWhatsAppTemplates()
        setSelectedWhatsAppTemplate(cleanName)
        setIsCustomCampaign(false)
        setIsEditingTemplate(false)
        setCustomCampaignName(cleanName)
        form.setFieldsValue({ whatsAppTemplate: cleanName })
      })
      .catch((err) => {
        openNotification('error', err.message || 'Failed to save template')
      })
  }

  const handleDeleteTemplate = (tmplName) => {
    deleteTemplateMutation({ variables: { name: tmplName } })
      .then(() => {
        openNotification('success', 'Template deleted successfully!')
        refetchWhatsAppTemplates()
        if (selectedWhatsAppTemplate === tmplName) {
          setSelectedWhatsAppTemplate(undefined)
          setDynamicParams([])
          form.setFieldsValue({ whatsAppTemplate: undefined })
        }
      })
      .catch((err) => {
        openNotification('error', err.message || 'Failed to delete template')
      })
  }

  const confirmDeleteTemplate = (tmplName) => {
    Modal.confirm({
      title: 'Delete WhatsApp Template',
      content: `Are you sure you want to delete template "${tmplName}"?`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        handleDeleteTemplate(tmplName)
      },
    })
  }

  const handleAddParam = () => {
    const nextIdx = dynamicParams.length + 1
    const suggestedLabels = ['Name', 'Date', 'Link']
    const suggestedLabel = suggestedLabels[nextIdx - 1] || `Parameter ${nextIdx}`
    const newParam = {
      id: `p_${Date.now()}`,
      label: suggestedLabel,
      value: '',
    }
    setDynamicParams((prev) => [...prev, newParam])
  }

  const handleRemoveParam = (idToRemove) => {
    setDynamicParams((prev) => prev.filter((p) => p.id !== idToRemove))
  }

  const handleParamValueChange = (id, newVal) => {
    setDynamicParams((prev) =>
      prev.map((p) => (p.id === id ? { ...p, value: newVal } : p))
    )
  }

  const handleParamLabelChange = (id, newLabel) => {
    setDynamicParams((prev) =>
      prev.map((p) => (p.id === id ? { ...p, label: newLabel } : p))
    )
  }

  const insertUserTag = (tag) => {
    if (dynamicParams.length === 0) {
      const tagName = tag.replace(/[{}]/g, '')
      const label = tagName.charAt(0).toUpperCase() + tagName.slice(1)
      setDynamicParams([{ id: `p_${Date.now()}`, label, value: tag }])
      return
    }
    const targetIdx = activeParamIndex < dynamicParams.length ? activeParamIndex : 0
    const targetParam = dynamicParams[targetIdx]
    if (targetParam) {
      handleParamValueChange(targetParam.id, `${targetParam.value}${tag}`)
    }
  }

  let newValue = value

  if (error) return <Page404 />

  const handleValue = (content, delta, source, editor) => {
    newValue = editor.getHTML()
  }

  const normFile = (e) => {
    setFileList([])
    if (Array.isArray(e)) {
      return e
    }
    return e && e.fileList
  }

  const customProps = {
    multiple: false,
    onRemove: () => setFileList([]),
    beforeUpload: (file) => {
      setFileList(file)
      return false
    },
    fileList,
  }

  const modules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ header: 1 }, { header: 2 }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ direction: 'rtl' }],
      [{ size: ['small', false, 'large', 'huge'] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ color: [] }, { background: [] }],
      [{ font: [] }],
      [{ align: [] }],
      ['clean'],
    ],
    keyboard: {
      bindings: {
        'list autofill': {
          prefix: /^\s*()$/,
        },
      },
    },
  }

  const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'list',
    'bullet',
    'indent',
    'link',
    'color',
    'background',
    'image',
    'video',
    'align',
    'direction',
    'code-block',
    'formula',
  ]

  const handleSelect = (id) => {
    const template = data?.getBravoTemplatesData?.filter(
      (item) => item?.id === id
    )
    setValue(template[0].htmlContent)
    setTemplateId(id)
  }



  const submitForm = () => {
    console.log('=== submitForm called ===')
    console.log('type:', type, 'sendVia from form:', form.getFieldValue('sendVia'))
    form
      .validateFields()
      .then((values) => {
        console.log('=== validateFields passed, values:', values)
        const finalCampaignName = isCustomCampaign
          ? customCampaignName.trim()
          : selectedWhatsAppTemplate

        if (type === 'WHATSAPP' && !finalCampaignName) {
          form.setFields([
            {
              name: 'whatsAppTemplate',
              errors: ['Campaign Name is required!'],
            },
          ])
          return
        }

        const templateParams = dynamicParams.map((p) => (p.value ?? '').trim())

        values = {
          ...values,
          whatsAppTemplate: finalCampaignName,
          content: newValue,
          templateId,
          templateParams,
        }
        console.log('=== calling onSend with values:', values)
        onSend(values, resetForm)
      })
      .catch((info) => {
        console.log('=== Validate Failed:', info)
        const errorFields = info?.errorFields || []
        if (errorFields.length > 0) {
          const msgs = errorFields.map(f => f.errors?.[0]).filter(Boolean).join(', ')
          console.log('Validation errors:', msgs)
        }
      })
  }

  return (
    <Modal
      width='80%'
      maskClosable={false}
      visible={visible}
      className='feedback_form'
      title={'Send Notification'}
      footer={null}
      onCancel={() => {
        resetForm()
        onCancel()
      }}
    >
      <Form
        form={form}
        layout='vertical'
        name='form_in_modal'
        initialValues={{ modifier: 'public', sendVia: 'EMAIL' }}
      >
        <Form.Item
          name='sendVia'
          label='Notification Type'
          rules={[
            { required: true, message: 'Please select notification type!' },
          ]}
        >
          <Radio.Group
            onChange={(e) => {
              setType(e.target.value)
              setSelectedWhatsAppTemplate(undefined)
              setIsCustomCampaign(false)
              setDynamicParams([])
            }}
          >
            <Radio value={'SMS'}>SMS</Radio>
            <Radio value={'EMAIL'}>EMAIL</Radio>
            <Radio value={'BOTH'}>BOTH</Radio>
            <Radio value={'TEMPLATE'}>TEMPLATE</Radio>
            <Radio value={'WHATSAPP'}>WHATSAPP</Radio>
          </Radio.Group>
        </Form.Item>

        {type === 'WHATSAPP' ? (
          <>
            <Form.Item
              label='Select WhatsApp Template / Campaign'
              required
              style={{ marginBottom: '16px' }}
            >
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                <Form.Item
                  name='whatsAppTemplate'
                  noStyle
                  rules={[
                    { required: true, message: 'Please select WhatsApp template!' },
                  ]}
                >
                  <Select
                    showSearch
                    optionLabelProp='label'
                    getPopupContainer={(trigger) => trigger.parentElement}
                    placeholder='Search & Select WhatsApp Template / Campaign'
                    style={{ flex: 1, fontSize: '14px', height: '36px' }}
                    value={selectedWhatsAppTemplate}
                    onChange={handleTemplateChange}
                    optionFilterProp='value'
                    filterOption={(input, option) =>
                      (option?.value || '').toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {allWhatsAppTemplates.map((templateName) => {
                      const isSaved = templatesMap[templateName]?.isSaved
                      return (
                        <Option key={templateName} value={templateName} label={templateName}>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              width: '100%',
                            }}
                          >
                            <span style={{ fontSize: '13px' }}>{templateName}</span>
                            {isSaved && (
                              <div
                                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                <span style={{ fontSize: '11px', color: '#52c41a' }}>(Saved)</span>
                                <Tooltip title={`Delete ${templateName}`}>
                                  <Button
                                    type='text'
                                    danger
                                    size='small'
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      height: '22px',
                                      width: '22px',
                                      padding: 0,
                                      margin: 0,
                                      marginBottom: 0,
                                      borderRadius: '4px',
                                    }}
                                    icon={<DeleteOutlined style={{ fontSize: '13px' }} />}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      e.preventDefault()
                                      confirmDeleteTemplate(templateName)
                                    }}
                                  />
                                </Tooltip>
                              </div>
                            )}
                          </div>
                        </Option>
                      )
                    })}
                    <Option
                      key='CUSTOM_CAMPAIGN'
                      value='CUSTOM_CAMPAIGN'
                      label='+ Dynamic Campaign Name & Custom Parameters'
                      style={{ fontWeight: 'bold', color: '#1890ff' }}
                    >
                      + Dynamic Campaign Name & Custom Parameters
                    </Option>
                  </Select>
                </Form.Item>

                <Button
                  type='primary'
                  ghost
                  icon={<PlusOutlined />}
                  style={{
                    borderColor: '#25D366',
                    color: '#128C7E',
                    whiteSpace: 'nowrap',
                    marginBottom: 0,
                    height: '36px',
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                  onClick={() => handleTemplateChange('CUSTOM_CAMPAIGN')}
                >
                  Add Template
                </Button>

                {selectedWhatsAppTemplate && selectedWhatsAppTemplate !== 'CUSTOM_CAMPAIGN' && (
                  <>
                    {isEditingTemplate ? (
                      <Button
                        style={{
                          borderColor: '#d9d9d9',
                          color: '#595959',
                          whiteSpace: 'nowrap',
                          marginBottom: 0,
                          height: '36px',
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}
                        onClick={() => {
                          setIsEditingTemplate(false)
                          handleTemplateChange(selectedWhatsAppTemplate)
                        }}
                      >
                        Cancel Edit
                      </Button>
                    ) : (
                      <Button
                        type='default'
                        icon={<EditOutlined />}
                        style={{
                          borderColor: '#1890ff',
                          color: '#1890ff',
                          whiteSpace: 'nowrap',
                          marginBottom: 0,
                          height: '36px',
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}
                        onClick={() => setIsEditingTemplate(true)}
                      >
                        Edit Template
                      </Button>
                    )}
                  </>
                )}

                {templatesMap[selectedWhatsAppTemplate]?.isSaved && (
                  <Popconfirm
                    title={`Are you sure you want to delete template "${selectedWhatsAppTemplate}"?`}
                    onConfirm={() => handleDeleteTemplate(selectedWhatsAppTemplate)}
                    okText='Yes, Delete'
                    cancelText='Cancel'
                  >
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      style={{
                        whiteSpace: 'nowrap',
                        marginBottom: 0,
                        height: '36px',
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}
                    >
                      Delete Template
                    </Button>
                  </Popconfirm>
                )}
              </div>
            </Form.Item>

            {isCustomCampaign && (
              <Form.Item
                label='Custom Campaign Name (Aisensy / WhatsApp Template Name)'
                required
                style={{ marginBottom: '16px' }}
              >
                <Input
                  placeholder='Enter Campaign Name (e.g. diwali_offer_welcome)'
                  value={customCampaignName}
                  onChange={(e) => setCustomCampaignName(e.target.value)}
                />
              </Form.Item>
            )}

            {selectedWhatsAppTemplate && (isEditingTemplate || isCustomCampaign) && (
              <Card
                size='small'
                style={{
                  borderRadius: '8px',
                  marginBottom: '16px',
                  marginTop: '10px',
                  border: '1px solid #d9d9d9',
                  background: '#fafafa',
                }}
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <PictureOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                      <span style={{ fontWeight: 'bold' }}>Header Media Configuration</span>
                    </div>
                  </div>
                }
              >
                <div style={{ padding: '4px 0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 500, color: '#262626', fontSize: '13px' }}>
                      Does this template have an image header?
                    </span>
                    <Radio.Group
                      value={hasHeaderImage ? 'YES' : 'NO'}
                      onChange={(e) => {
                        const isYes = e.target.value === 'YES'
                        setHasHeaderImage(isYes)
                        if (!isYes) {
                          setTemplateImageFile(null)
                          setImagePreviewUrl('')
                        } else if (templateImageUrl) {
                          setImagePreviewUrl(templateImageUrl)
                        }
                      }}
                    >
                      <Radio value='NO'>No (Text-Only)</Radio>
                      <Radio value='YES'>Yes (Image Header)</Radio>
                    </Radio.Group>
                  </div>

                  {hasHeaderImage && (
                    <div
                      style={{
                        marginTop: '12px',
                        padding: '12px',
                        background: '#ffffff',
                        borderRadius: '6px',
                        border: '1px dashed #1890ff',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <Upload
                          accept='image/jpeg,image/png,image/jpg'
                          showUploadList={false}
                          beforeUpload={async (file) => {
                            const isImage = file.type.startsWith('image/') || /\.(jpe?g|png|webp)$/i.test(file.name)
                            if (!isImage) {
                              openNotification('error', 'Please select an image file (JPG, PNG)!')
                              return Upload.LIST_IGNORE
                            }
                            const isLt5M = file.size / 1024 / 1024 <= 5
                            if (!isLt5M) {
                              openNotification('error', 'Image must be smaller than 5 MB!')
                              return Upload.LIST_IGNORE
                            }
                            try {
                              const normalized = await processImageForWhatsApp(file)
                              setTemplateImageFile(normalized)
                              const reader = new FileReader()
                              reader.onload = (uploadEvent) => {
                                setImagePreviewUrl(uploadEvent.target.result)
                              }
                              reader.readAsDataURL(normalized)
                            } catch (e) {
                              setTemplateImageFile(file)
                              const reader = new FileReader()
                              reader.onload = (uploadEvent) => {
                                setImagePreviewUrl(uploadEvent.target.result)
                              }
                              reader.readAsDataURL(file)
                            }
                            return false
                          }}
                        >
                          <Button icon={<UploadOutlined />} style={{ borderColor: '#1890ff', color: '#1890ff' }}>
                            {imagePreviewUrl ? 'Change Header Image' : 'Select Image from System (Max 5 MB)'}
                          </Button>
                        </Upload>

                        {templateImageFile && (
                          <Tag color='cyan' style={{ padding: '4px 8px', fontSize: '12px' }}>
                            Selected: {templateImageFile.name} ({(templateImageFile.size / 1024 / 1024).toFixed(2)} MB)
                          </Tag>
                        )}

                        {!templateImageFile && templateImageUrl && (
                          <Tag color='blue' style={{ padding: '4px 8px', fontSize: '12px' }}>
                            Current Image Saved on S3
                          </Tag>
                        )}

                        {imagePreviewUrl && (
                          <Button
                            size='small'
                            type='link'
                            danger
                            icon={<CloseCircleOutlined />}
                            onClick={() => {
                              setTemplateImageFile(null)
                              setImagePreviewUrl('')
                              setTemplateImageUrl('')
                            }}
                          >
                            Remove Image
                          </Button>
                        )}
                      </div>

                      {imagePreviewUrl && (
                        <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div
                            style={{
                              width: '120px',
                              height: '75px',
                              border: '1px solid #d9d9d9',
                              borderRadius: '6px',
                              overflow: 'hidden',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: '#f5f5f5',
                            }}
                          >
                            <img
                              src={imagePreviewUrl}
                              alt='Header preview'
                              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            />
                          </div>
                          <div style={{ fontSize: '12px', color: '#595959' }}>
                            <div style={{ fontWeight: 600, color: '#262626' }}>Header Image Preview</div>
                            <div style={{ color: '#8c8c8c' }}>
                              Uploaded to S3 upon saving template and sent as media header in WhatsApp.
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            )}

            {selectedWhatsAppTemplate && !isEditingTemplate && selectedWhatsAppTemplate !== 'CUSTOM_CAMPAIGN' && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 14px',
                  background: hasHeaderImage ? '#e6f7ff' : '#f5f5f5',
                  border: hasHeaderImage ? '1px solid #91d5ff' : '1px solid #d9d9d9',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  marginTop: '10px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <PictureOutlined style={{ fontSize: '18px', color: hasHeaderImage ? '#1890ff' : '#8c8c8c' }} />
                  <div>
                    <span style={{ fontWeight: 600, fontSize: '13px', color: hasHeaderImage ? '#096dd9' : '#595959' }}>
                      {hasHeaderImage ? 'Image Header Template' : 'Text-Only Template (No Image Header)'}
                    </span>
                    <span style={{ fontSize: '12px', color: '#8c8c8c', marginLeft: '8px' }}>
                      {hasHeaderImage
                        ? 'WhatsApp notification will include this image header media.'
                        : 'WhatsApp notification will be sent text-only without media header.'}
                    </span>
                  </div>
                </div>
                {hasHeaderImage && imagePreviewUrl && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img
                      src={imagePreviewUrl}
                      alt='Thumbnail'
                      style={{
                        width: '40px',
                        height: '28px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        border: '1px solid #91d5ff',
                      }}
                    />
                    <a
                      href={imagePreviewUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      style={{ fontSize: '12px' }}
                    >
                      View Image
                    </a>
                  </div>
                )}
              </div>
            )}

            {selectedWhatsAppTemplate && !isEditingTemplate && dynamicParams.length === 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  background: '#f6ffed',
                  border: '1px solid #b7eb8f',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  marginTop: '10px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#389e0d' }}>
                  <CheckCircleOutlined style={{ fontSize: '16px' }} />
                  <span style={{ fontSize: '13px', fontWeight: 500 }}>
                    This template has no parameters. Ready to send notification.
                  </span>
                </div>
                <Button
                  size='small'
                  type='link'
                  icon={<EditOutlined />}
                  onClick={() => setIsEditingTemplate(true)}
                  style={{ color: '#1890ff', padding: 0 }}
                >
                  Edit / Add Parameters
                </Button>
              </div>
            )}

            {selectedWhatsAppTemplate && (isEditingTemplate || dynamicParams.length > 0) && (
              <Card
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 'bold' }}>Template Parameters</span>
                    {(isEditingTemplate || isCustomCampaign) && (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Button
                          type='dashed'
                          size='small'
                          icon={<PlusOutlined />}
                          onClick={handleAddParam}
                          style={{ borderColor: '#25D366', color: '#128C7E' }}
                        >
                          Add Parameter
                        </Button>
                        <Button
                          type='primary'
                          size='small'
                          icon={<SaveOutlined />}
                          onClick={handleSaveTemplate}
                          loading={savingTemplate}
                          style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
                        >
                          Save Template
                        </Button>
                      </div>
                    )}
                  </div>
                }
                size='small'
                style={{ borderRadius: '8px', marginBottom: '16px', marginTop: '10px' }}
              >
                <div style={{ marginBottom: '12px', background: '#f5f5f5', padding: '8px 12px', borderRadius: '6px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#595959', marginRight: '8px' }}>
                    <TagOutlined /> Insert User Dynamic Tag:
                  </span>
                  <Tag
                    color='blue'
                    style={{ cursor: 'pointer' }}
                    onClick={() => insertUserTag('{firstName}')}
                  >
                    + {'{firstName}'}
                  </Tag>
                  <Tag
                    color='geekblue'
                    style={{ cursor: 'pointer' }}
                    onClick={() => insertUserTag('{lastName}')}
                  >
                    + {'{lastName}'}
                  </Tag>
                  <Tag
                    color='purple'
                    style={{ cursor: 'pointer' }}
                    onClick={() => insertUserTag('{clientCode}')}
                  >
                    + {'{clientCode}'}
                  </Tag>
                  <Tag
                    color='green'
                    style={{ cursor: 'pointer' }}
                    onClick={() => insertUserTag('{phone}')}
                  >
                    + {'{phone}'}
                  </Tag>
                </div>

                {dynamicParams.length === 0 ? (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '24px 12px',
                      color: '#8c8c8c',
                      background: '#fafafa',
                      borderRadius: '6px',
                      border: '1px dashed #d9d9d9',
                      marginBottom: '12px',
                    }}
                  >
                    <p style={{ margin: 0, fontSize: '13px', color: '#595959' }}>
                      No parameters added yet.
                    </p>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#8c8c8c' }}>
                      Click <strong>+ Add Parameter</strong> above or click a dynamic tag to add variables (e.g. <code>{'{{1}}'}</code>, <code>{'{{2}}'}</code>).
                    </p>
                  </div>
                ) : (
                  dynamicParams.map((p, idx) => (
                    <div
                      key={p.id}
                      onClick={() => setActiveParamIndex(idx)}
                      style={{
                        marginBottom: '12px',
                        padding: '10px 12px',
                        border: activeParamIndex === idx ? '1px solid #1890ff' : '1px solid #f0f0f0',
                        borderRadius: '8px',
                        background: activeParamIndex === idx ? '#e6f7ff' : '#fafafa',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', flex: 1, marginRight: '8px' }}>
                          <span style={{ fontWeight: 'bold', fontSize: '13px', marginRight: '6px', color: '#1890ff' }}>
                            {idx + 1}.
                          </span>
                          {isEditingTemplate ? (
                            <Input
                              size='small'
                              value={p.label}
                              onChange={(e) => handleParamLabelChange(p.id, e.target.value)}
                              placeholder='Parameter Label (e.g. Name, Date, Link)'
                              suffix={
                                <Tooltip title='Click to edit parameter label'>
                                  <EditOutlined style={{ color: '#1890ff', fontSize: '13px', cursor: 'pointer' }} />
                                </Tooltip>
                              }
                              style={{
                                maxWidth: '240px',
                                fontWeight: 'bold',
                                fontSize: '13px',
                                borderRadius: '4px',
                                backgroundColor: '#ffffff',
                                border: '1px solid #d9d9d9',
                              }}
                            />
                          ) : (
                            <span style={{ fontWeight: 'bold', fontSize: '13px', color: '#262626' }}>
                              {p.label || `Parameter ${idx + 1}`}
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Tag color='cyan' style={{ margin: 0, fontWeight: 'bold' }}>{"{{" + (idx + 1) + "}}"}</Tag>
                          {isEditingTemplate && (
                            <Tooltip title='Remove Parameter'>
                              <Button
                                type='text'
                                danger
                                size='small'
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  height: '24px',
                                  width: '24px',
                                  padding: 0,
                                  margin: 0,
                                  marginBottom: 0,
                                  borderRadius: '4px',
                                }}
                                icon={<DeleteOutlined style={{ fontSize: '14px' }} />}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleRemoveParam(p.id)
                                }}
                              />
                            </Tooltip>
                          )}
                        </div>
                      </div>
                      <Input
                        placeholder={
                          idx === 0
                            ? 'Enter value (e.g. {firstName})'
                            : idx === 1
                            ? 'Enter value (e.g. 05-08-2026)'
                            : idx === 2
                            ? 'Enter value (e.g. https://...)'
                            : `Enter parameter {{${idx + 1}}} value`
                        }
                        value={p.value}
                        onChange={(e) => handleParamValueChange(p.id, e.target.value)}
                      />
                    </div>
                  ))
                )}
              </Card>
            )}
          </>
        ) : type === 'TEMPLATE' ? (
          <>
            <Form.Item
              name='templateId'
              label='Select Template'
              preserve={false}
              rules={[
                { required: true, message: 'Please select Template type!' },
              ]}
            >
              <Select
                showSearch
                getPopupContainer={(trigger) => trigger.parentElement}
                className='gx-mr-3 gx-select-sm'
                defaultValue=''
                onChange={(value) => handleSelect(value)}
                style={{ minWidth: 100 }}
                loading={loading}
                optionFilterProp='children'
                filterOption={(input, option) =>
                  (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                <Option value='' disabled>
                  All Templates
                </Option>
                {data?.getBravoTemplatesData?.map((template) => (
                  <Option key={template?.id} value={template?.id}>{template?.name}</Option>
                ))}
              </Select>
            </Form.Item>
            <div dangerouslySetInnerHTML={{ __html: value }} />
          </>
        ) : (
          <>
            <ReactQuill
              className='abcdef'
              theme='snow'
              value={value}
              onChange={handleValue}
              modules={modules}
              formats={formats}
            />
          </>
        )}
        {type !== 'SMS' && (
          <Form.Item
            name='file'
            label='Attachment'
            valuePropName='fileList'
            getValueFromEvent={normFile}
          >
            <Upload
              name='attachment'
              listType='text'
              {...customProps}
              accept='.jpeg,.png,.jpg,.pdf,.docx,.doc'
            >
              <Button>
                <UploadOutlined /> Click to upload
              </Button>
            </Upload>
          </Form.Item>
        )}

        <div className='footer-btn-div'>
          <Button
            className='footer-cancel-btn'
            onClick={() => {
              resetForm()
              onCancel()
            }}
          >
            Cancel
          </Button>
          <Button
            className='footer-submit-btn'
            onClick={(e) => {
              console.log('=== Send Notification button CLICKED ===')
              e.preventDefault()
              e.stopPropagation()
              submitForm()
            }}
            type='primary'
          >
            Send Notification
          </Button>
        </div>
      </Form>
    </Modal>
  )
}

const CreateGroupModal = (props) => {
  return (
    <div>
      <CollectionCreateForm {...props} />
    </div>
  )
}

export default CreateGroupModal
